/**
 * sanitizeMarkdownHtml — defense-in-depth sanitizer for marked.js output.
 *
 * Mirrors the backend allowlist in
 * `Core\Communication\Base\Support\ContentNormalizer`. Drops any element/
 * attribute the announcement editor could not have produced, in case stored
 * content bypassed the backend normalizer.
 *
 * DOM-based: parses via `DOMParser`, walks the tree, and rewrites/removes
 * disallowed nodes. Robust against attribute values that contain `>`, mixed
 * case, or other oddities a regex parser would mishandle.
 *
 * For each `<span data-color="…">` node with a valid color name, the
 * matching Tailwind class is added inline so no CSS rules tied to
 * `[data-color]` selectors are needed.
 */
import { TEXT_COLOR_NAME_SET, textColorClass } from '@/components/ui/textarea/text-colors'

// Tags marked.js can legitimately emit. Anything else is replaced with its
// text content (or dropped for script/style/iframe/etc — see DROP_TAGS).
const STRUCTURAL_TAGS = new Set([
  'p', 'br', 'hr', 'strong', 'em', 'b', 'i', 'u', 's', 'del', 'ins',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'blockquote',
  'pre', 'code',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
])

// Tags whose entire subtree must be removed (no text leakage).
const DROP_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input',
  'textarea', 'select', 'button', 'link', 'meta', 'noscript', 'svg', 'math',
])

// Allowed schemes for `<a href>`. Mirrors backend safe-href set.
const SAFE_HREF_RE = /^(?:https?:\/\/|mailto:|tel:|#|\/|\.{1,2}\/)/i

const PROTOCOL_RELATIVE_RE = /^\/\//

function isExternalHttp(href: string): boolean {
  return /^https?:\/\//i.test(href)
}

function sanitizeAnchor(el: Element): Element | DocumentFragment | null {
  const href = el.getAttribute('href') ?? ''
  // Block protocol-relative URLs — they default to https/http but bypass
  // the explicit-scheme allowlist semantics in some user-agents.
  if (PROTOCOL_RELATIVE_RE.test(href) || !SAFE_HREF_RE.test(href)) {
    return null // signal: replace with text
  }

  const owner = el.ownerDocument!
  const next = owner.createElement('a')
  next.setAttribute('href', href)

  if (isExternalHttp(href)) {
    next.setAttribute('target', '_blank')
    next.setAttribute('rel', 'noopener noreferrer')
  }

  while (el.firstChild) {
    next.appendChild(el.firstChild)
  }
  return next
}

function sanitizeSpan(el: Element): Element | null | 'unwrap' {
  const color = el.getAttribute('data-color')
  if (color === null) {
    // Plain spans aren't part of the editor output — unwrap to text.
    return 'unwrap'
  }
  if (!TEXT_COLOR_NAME_SET.has(color)) {
    return 'unwrap'
  }

  const owner = el.ownerDocument!
  const next = owner.createElement('span')
  next.setAttribute('data-color', color)
  const cls = textColorClass(color)
  if (cls) {
    next.setAttribute('class', cls)
  }
  while (el.firstChild) {
    next.appendChild(el.firstChild)
  }
  return next
}

function sanitizeStructural(el: Element): Element {
  // Drop every attribute — marked emits structural tags without attributes
  // for our use case (no language hints, ids, etc.). If we ever need to keep
  // an attribute, allowlist it here explicitly.
  const owner = el.ownerDocument!
  const next = owner.createElement(el.tagName.toLowerCase())
  while (el.firstChild) {
    next.appendChild(el.firstChild)
  }
  return next
}

function sanitizeNode(node: Node): Node[] {
  if (node.nodeType === 3 /* TEXT_NODE */) {
    return [node]
  }
  if (node.nodeType !== 1 /* ELEMENT_NODE */) {
    return []
  }

  const el = node as Element
  const tag = el.tagName.toLowerCase()

  if (DROP_TAGS.has(tag)) {
    return []
  }

  // Recurse first so children are sanitized before re-attaching.
  const sanitizedChildren: Node[] = []
  for (const child of Array.from(el.childNodes)) {
    sanitizedChildren.push(...sanitizeNode(child))
  }
  // Replace original children with sanitized versions.
  while (el.firstChild) el.removeChild(el.firstChild)
  for (const c of sanitizedChildren) el.appendChild(c)

  if (tag === 'a') {
    const result = sanitizeAnchor(el)
    if (!result) {
      return Array.from(el.childNodes) // unwrap to text
    }
    return [result]
  }

  if (tag === 'span') {
    const result = sanitizeSpan(el)
    if (result === 'unwrap' || result === null) {
      return Array.from(el.childNodes)
    }
    return [result]
  }

  if (STRUCTURAL_TAGS.has(tag)) {
    return [sanitizeStructural(el)]
  }

  // Unknown tag: drop wrapper, keep inner text/children.
  return Array.from(el.childNodes)
}

/**
 * Sanitize HTML string emitted by marked.js. Returns a string suitable for
 * v-html in browser contexts. Falls back to the input unchanged on SSR
 * (no DOMParser available) — the backend normalizer is the authoritative
 * security boundary; this layer is defense-in-depth.
 */
export function sanitizeMarkdownHtml(html: string): string {
  if (typeof DOMParser === 'undefined') {
    return html
  }

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  const body = doc.body

  const sanitized: Node[] = []
  for (const child of Array.from(body.childNodes)) {
    sanitized.push(...sanitizeNode(child))
  }

  // Serialize back to string.
  const out = doc.createElement('div')
  for (const n of sanitized) out.appendChild(n)
  return out.innerHTML
}
